// habitTodayClaims.js — "you missed <habit> today" when that habit IS logged
// today (2026-10-20). A demo-account brief said "You missed Gym, Read, and
// Drink water today" while all three were logged. Pure, shared: the check-in
// audit (api/lib/checkin-audit.js habitClaimIssues/stripHabitClaims) and the
// Home brief (Scorify.jsx generateBriefing) run the same detector.
//
// `items`: [{ name, label, loggedToday }] — kai.habits.items on the server,
// the same three fields on the client. Only loggedToday habits can be hit.

const esc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Names the model might use for a habit: its label ("Read 20 min"), the label
// without a trailing amount ("Read"), and the stored name/slug ("reading").
export function habitNameVariants(h) {
  const out = new Set();
  for (const raw of [h && h.label, h && h.name]) {
    const s = String(raw || '').trim();
    if (!s) continue;
    out.add(s);
    const bare = s.replace(/\s+\d[\d.,]*\s*(?:min(?:ute)?s?|hours?|hrs?|h|glasses|cups|pages|steps|k|x)?\s*$/i, '').trim();
    if (bare && bare.length >= 3) out.add(bare);
  }
  return [...out];
}

const S = '[^.!?\\n]'; // stay inside one sentence
// …and inside one clause: "missed Gym on Saturday, but today is logged" is not a today claim.
const C = '(?:(?!\\b(?:but|though|although|however|yet today)\\b)[^.!?\\n])';
const TODAY = String.raw`(?:today|yet|so far today|this morning)`;
const NOT_DONE = String.raw`(?:hasn['’]t|has not|haven['’]t|have not|didn['’]t|did not|not|never|isn['’]t|is not)\s+(?:been\s+|yet\s+|already\s+)?(?:logged|done|ticked|checked off|hit|tracked|completed)`;

function patternsFor(name) {
  const n = `(?<![A-Za-z0-9])${esc(name)}(?![A-Za-z0-9])`;
  return [
    // "You missed Gym, Read, and Drink water today"
    new RegExp(`\\bmiss(?:ed|ing)\\b${C}{0,80}?${n}${C}{0,80}?\\b${TODAY}\\b`, 'i'),
    // "Gym hasn't been logged today"
    new RegExp(`${n}${S}{0,60}?\\b${NOT_DONE}\\b${S}{0,30}?\\b${TODAY}\\b`, 'i'),
    // "you haven't logged Gym today" / "not done Gym yet"
    new RegExp(`\\b${NOT_DONE}\\b${S}{0,40}?${n}${S}{0,40}?\\b${TODAY}\\b`, 'i'),
    // "Gym is still open today" / "Gym still to do today"
    new RegExp(`${n}${S}{0,30}?\\bstill\\s+(?:open|to do|undone|unlogged|pending)\\b`, 'i'),
  ];
}

/** Habits that are logged today but that `text` says were missed / not done today. */
export function missedTodayHits(text, items = []) {
  const t = String(text || '');
  if (!t) return [];
  const hits = [];
  for (const h of Array.isArray(items) ? items : []) {
    if (!h || !h.loggedToday) continue;
    if (habitNameVariants(h).some(nm => patternsFor(nm).some(re => re.test(t)))) hits.push(h.label || h.name);
  }
  return hits;
}
